import React, { useState, useContext, useEffect } from 'react';
import { CardSurface, sparkToast, useReadOnly } from '@spark';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { colors } from '@/shared/tokens/colors';
import { ComparisonModeOption, TaskType, isGenerativeTask, getGenerativeOutputNoun } from '@/shared/domain/taskTypes';
import {
    classificationTaskLabels,
    missingOptimizeOnLabel,
    rareClassEligibleLabels,
    trainingDefaultsFromLabelPriority,
} from '@/shared/domain/labelPriority';
import OptimizeOnLabelMissingModal from '@/shared/components/OptimizeOnLabelMissingModal';
import Submit from './finetuning/Submit';
import Advanced from './finetuning/Advanced';
import ModelSummarySection from './finetuning/ModelSummarySection';
import JudgeCriteriaSelector from './finetuning/JudgeCriteriaSelector';
import MetricsCriteriaSelector from './finetuning/MetricsCriteriaSelector';
import BaseModelSelector from './finetuning/BaseModelSelector';
import { GlobalContext } from '@/app/providers/globalState';
import { distillation_in_progress } from '@/shared/constants/taskStatuses';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import { useTrainingProgress } from '@/features/trainings/hooks/useTrainingProgress';
import { validateTrainingStart, startTrainingProcess, scheduleUsecaseRefreshAfterTrainingStart, deriveIncludeInstructionInTraining } from '@/features/trainings/utils/trainingUtils';
import {
    armAutoTrain,
    clearAutoTrainNotifyModalShown,
    listActiveDataFlowsForUsecase,
    markAutoTrainNotifyModalShown,
    resolveArmResult,
    resolveDataFlowsGate,
    validateAutoTrainConfig,
} from '@/features/trainings/utils/autoTrain';
import AutoTrainScheduleSection from '@/features/trainings/components/AutoTrainScheduleSection';
import DataFlowsRunningModal from './finetuning/DataFlowsRunningModal';
import TrainBlockedModal from './finetuning/TrainBlockedModal';
import { getDefaultTrainingParams, FINAL_TRAINING_STATUSES } from '@/features/trainings/constants';
import { data_from_server_post } from '@/shared/http/client';
import { getLabelTypeMismatchMessages } from '@/features/trainings/utils/labelMismatchUtils';
import LoadingTaskData from '@/features/tasks/components/LoadingTaskData';
import ModelProcessingModal from '@/features/tasks/components/Task/ModelProcessingModal';
import BenchmarkOverlapModal from './finetuning/BenchmarkOverlapModal';
import UnlabeledDataWarningModal from './finetuning/UnlabeledDataWarningModal';
import { TaskDrawerHeaderContext } from '@/features/tasks/components/Task/taskDrawerContext';
import { taskUrlForUsecase } from '@/features/tasks/utils/navigation';
import { UNLABELED } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import { useAuth } from '@/features/auth/AuthContext';
import { getEnabledJsonSchemaError } from '@/features/model-setup';
import '@/features/trainings/styles/train.css';

// Inline link used inside train-blocked messages. Renders a keyboard-accessible
// blue underlined "link" span; callers pass surrounding text/whitespace.
const TrainBlockedLink = ({ onClick, children, style }) => (
    <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onClick();
        }}
        style={{
            color: colors.buttonsBlue,
            cursor: 'pointer',
            textDecoration: 'underline',
            fontWeight: 600,
            ...style,
        }}
    >
        {children}
    </span>
);

export const TrainFromSpecificUsecase = ({
    moveToNextStep,
    moveToScreen,
    embeddedInTaskDrawer = false,
    onDataOverviewRefresh,
    /** `'page'` is the Model Summary tab / wizard; `'summary-modal'` is the kebab Retrain dialog. */
    layout = 'page',
    /** Lets a host (Retrain modal) bind Let's Train without duplicating submit logic. */
    onTrainActionChange,
    /** Fired when a new training version is actually kicked off. */
    onTrainingStarted,
    /** Fired before blocked-message links navigate away (close the Retrain modal). */
    onNavigateAway,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        getCurrentUsecase,
        refreshUsecases,
        updateStatus,
        updateVersion,
        setCurrentUsecaseId,
        currentSubGroup,
        updateUsecase,
        progressStatus,
    } = useContext(GlobalContext);

    const setDrawerHeaderNext = useContext(TaskDrawerHeaderContext);
    const { role, user } = useAuth();
    const { readOnly } = useReadOnly();

    // Initial usecase from props or context
    const [localUsecase, setLocalUsecase] = useState(null);

    // Get usecase from context only once on mount or when dependencies change
    useEffect(() => {
        const contextUsecase = getCurrentUsecase();
        if (contextUsecase) {
            setLocalUsecase(contextUsecase);
        }
    }, [getCurrentUsecase]);

    // Handle URL parameters on refresh
    useEffect(() => {
        if (!localUsecase) {
            const urlParams = new URLSearchParams(location.search);
            const usecaseId = urlParams.get('usecaseId');

            if (usecaseId) {
                setCurrentUsecaseId(usecaseId);
                refreshUsecases();
            }
        }
    }, [location, localUsecase, setCurrentUsecaseId, refreshUsecases]);

    const { availableProviders, isLoading: isProvidersLoading } = useCommercialModels();

    // Message of the blocked-training warning modal (TrainBlockedModal).
    // Set by every failed training-start validation; cleared by "Got it" or
    // by navigating away via one of the message links below.
    const [trainBlockedMessage, setTrainBlockedMessage] = useState('');
    // `{ label, taskLabels }` while the "label to optimize on isn't in this
    // task" blocker is open: the Advanced "Optimize on" pick names a label
    // the task no longer has (labels edited since the step was seeded).
    const [optimizeOnMismatch, setOptimizeOnMismatch] = useState(null);

    /**
     * Blocks a training start whose "Optimize on" label is not one of the
     * task's labels. Returns true when blocked (modal opened).
     */
    const blockOnMissingOptimizeOnLabel = (rareClassLabel, usecase) => {
        const taskLabels = classificationTaskLabels(usecase);
        const missing = missingOptimizeOnLabel(rareClassLabel, taskLabels);
        if (!missing) return false;
        setOptimizeOnMismatch({ label: missing, taskLabels });
        return true;
    };

    // Deep-link straight to Settings > Model Providers (same pattern as the
    // Model Selection "Add a new key" footer); ``from`` powers the Back button.
    const handleGoToSettings = () => {
        setTrainBlockedMessage('');
        onNavigateAway?.();
        navigate('/settings?tab=providers', {
            state: { from: `${location.pathname}${location.search}${location.hash}` },
        });
    };

    const handleGoToDataCollection = () => {
        setTrainBlockedMessage('');
        onNavigateAway?.();
        if (!localUsecase) return;
        const url = taskUrlForUsecase(localUsecase, { objective: localUsecase._id });
        if (url) navigate(url);
    };

    const handleGoToDataSelection = () => {
        setTrainBlockedMessage('');
        onNavigateAway?.();
        if (!localUsecase) return;
        if (typeof moveToScreen === 'function') {
            moveToScreen(localUsecase._id, 'data-selection');
            return;
        }
        const url = taskUrlForUsecase(localUsecase, {
            objective: localUsecase._id,
            action: 'data-selection',
        });
        if (url) navigate(url);
    };

    // Open the Data Editor drawer focused on the Benchmark tab. Used when a
    // sub-task within a composed task is missing a benchmark and the user
    // needs to add one before training is allowed.
    const handleGoToBenchmark = () => {
        setTrainBlockedMessage('');
        onNavigateAway?.();
        if (!localUsecase) return;
        if (embeddedInTaskDrawer) {
            const next = new URLSearchParams(searchParams);
            next.set('task_id', localUsecase._id);
            next.set('action', 'data-selection');
            next.set('tab', 'benchmark');
            setSearchParams(next, { replace: true });
            return;
        }
        if (typeof moveToScreen === 'function') {
            moveToScreen(localUsecase._id, 'data-selection');
            return;
        }
        const url = taskUrlForUsecase(localUsecase, {
            objective: localUsecase._id,
            action: 'data-selection',
        });
        if (url) navigate(`${url}&tab=benchmark`);
    };

    const handleGoToModelConfiguration = () => {
        setTrainBlockedMessage('');
        onNavigateAway?.();
        if (!localUsecase) return;
        if (typeof moveToScreen === 'function') {
            moveToScreen(localUsecase._id, 'model-setup');
            return;
        }
        const url = taskUrlForUsecase(localUsecase, {
            objective: localUsecase._id,
            action: 'model-setup',
        });
        if (url) navigate(url);
    };

    // Open the Data Editor (train-set tab) with the "Unlabeled" label filter
    // pre-applied, so the user can review records with no labels.
    // Create-task wizard uses `?step=data-editor`; manage-task uses `?action=data-selection`.
    const handleReviewUnlabeled = () => {
        setTrainBlockedMessage('');
        onNavigateAway?.();
        if (!localUsecase) return;

        // Create-task wizard: switch step and seed labelFilter for DataSelection mount.
        if (searchParams.has('step')) {
            const next = new URLSearchParams();
            next.set('step', 'data-editor');
            next.set('usecaseId', localUsecase._id);
            const project = searchParams.get('project');
            if (project) next.set('project', project);
            next.set('tab', 'train-set');
            next.set('labelFilter', 'unlabeled');
            setSearchParams(next, { replace: true });
            return;
        }

        // Manage-task (embedded drawer / Task page tabs).
        if (embeddedInTaskDrawer) {
            const next = new URLSearchParams(searchParams);
            next.set('task_id', localUsecase._id);
            next.set('action', 'data-selection');
            next.set('tab', 'train-set');
            next.set('labelFilter', 'unlabeled');
            setSearchParams(next, { replace: true });
            return;
        }

        const url = taskUrlForUsecase(localUsecase, {
            objective: localUsecase._id,
            action: 'data-selection',
        });
        if (url) navigate(`${url}&tab=train-set&labelFilter=unlabeled`);
    };

    const defaultTrainingParams = getDefaultTrainingParams();
    // Generative tasks (summarization / content generation) share the decoder
    // training pipeline: GPU-only, teacher required, generative Advanced options.
    const isGenerative = isGenerativeTask(localUsecase?.task_type);

    // "Include prompt in training & evaluation" is configured on Model
    // Configuration > SpecificAI model ("Disable" per prompt field) and
    // derived from the usecase override fields at train time.
    const includeSystemPrompt = deriveIncludeInstructionInTraining(localUsecase);

    // The Accuracy and Classic metrics sections both persist the same
    // usecase.classic_metrics_config field. Keep the latest saved value here so
    // a save from one section is immediately visible to the other (otherwise a
    // quick second save could persist a stale copy and revert the first one
    // before refreshUsecases() delivers the updated usecase).
    const [classicMetricsOverride, setClassicMetricsOverride] = useState(null);
    const onMetricsCriteriaPersisted = (next) => {
        setClassicMetricsOverride(next);
        refreshUsecases?.();
    };

    const [state, setState] = useState({
        loading: false,
        userClicked: false,
        train: localUsecase?.benchmarks?.length > 0 ? 80 : defaultTrainingParams?.DEFAULT_TRAIN_RATIO,
        validation: localUsecase?.benchmarks?.length > 0 ? 20 : defaultTrainingParams?.DEFAULT_VALIDATION_RATIO,
        metricOptimization: defaultTrainingParams?.DEFAULT_METRIC_OPTIMIZATION,
        betaForFScore: 1,
        currentAppType: "existing-app",
        baseDistilledModels: [],
        trainingDevice: 'gpu',
        // Prediction strategy exposed in the Advanced section. ``automatic`` keeps
        // today's behavior (Optuna search picks). Strict values are forwarded to
        // ``create_distillation_event`` and pin the matching search parameter at
        // training time.
        predictionStrategy: 'automatic',
        // Train -> Advanced base model for the next training session only;
        // ``null`` is automatic.
        baseModel: null,
        // "Optimize on" (two-label single-label classification only): the
        // label the metric is computed for, sent as
        // ``model_evaluation_arguments.rare_class_label``. ``null`` = all labels.
        rareClassLabel: null,
    });

    // State to track dataset metadata including character count
    const [datasetMetadata, setDatasetMetadata] = useState(null);

    // State for benchmark overlap modal
    const [overlapModalState, setOverlapModalState] = useState({
        visible: false,
        overlappingCount: 0,
        totalCount: 0,
        isError: false,
    });

    // State for the "more than 10% unlabeled" warning modal (classification only)
    const [unlabeledModal, setUnlabeledModal] = useState({
        visible: false,
        unlabeledCount: 0,
        total: 0,
        percent: 0,
    });

    // State for the "data flows still running" gate modal: shown by Let's
    // Train while Generate-with-AI / Retrieval Agent flows are active, so the
    // user picks between waiting (auto-train) and training now.
    const [dataFlowsModal, setDataFlowsModal] = useState({ visible: false, flowCount: 0, finalizing: false });

    const [benchmarkMetadata, setBenchmarkMetadata] = useState(null);

    // State to track datasets info for multilabel checking
    const [datasetsInfo, setDatasetsInfo] = useState(null);

    // Fetch user settings to get default hardware device
    useEffect(() => {
        const fetchUserSettings = () => {
            data_from_server_post(
                "get_user_settings",
                localUsecase?.client_id || "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
                (result) => {
                    const trainingDevice = result.hardware_device || 'gpu';
                    setState(prev => ({
                        ...prev,
                        trainingDevice: trainingDevice
                    }));
                },
                (error) => {
                    console.error("Failed to fetch user settings:", error);
                }
            );
        };

        if (localUsecase?.client_id) {
            fetchUserSettings();
        }
    }, [localUsecase?.client_id]);

    // Helper function to fetch dataset metadata and datasets info
    const fetchDatasetMetadata = async () => {
        if (!localUsecase) return;

        try {
            const response = await new Promise((resolve, reject) => {
                data_from_server_post(
                    "datasets-and-benchmarks-meta-data",
                    {
                        client_id: localUsecase.client_id,
                        llm_usecase_id: localUsecase._id
                    },
                    resolve,
                    reject
                );
            });
            setDatasetMetadata(response?.dataset || null);
            setBenchmarkMetadata(response?.benchmark || null);
            setDatasetsInfo(response?.datasets_info || null);
        } catch (error) {
            console.error('Error fetching dataset metadata:', error);
            setDatasetMetadata(null);
            setDatasetsInfo(null);
        }
    };

    // Update state when localUsecase changes
    useEffect(() => {
        if (localUsecase) {
            const currentDefaultTrainingParams = getDefaultTrainingParams();
            setState(prev => ({
                ...prev,
                train: localUsecase.benchmarks?.length > 0 ? 80 : currentDefaultTrainingParams?.DEFAULT_TRAIN_RATIO,
                validation: localUsecase.benchmarks?.length > 0 ? 20 : currentDefaultTrainingParams?.DEFAULT_VALIDATION_RATIO,
            }));

            // Fetch dataset metadata and datasets info when usecase changes
            fetchDatasetMetadata();
        }
    }, [localUsecase]);

    useEffect(() => {
        setState(prev => ({ ...prev, baseModel: null }));
    }, [localUsecase?._id]);

    // Seed the Advanced defaults (metric, beta, "Optimize on") from the
    // Business Goals "What matters most" choice — once per task, so a
    // background usecase refresh never overwrites what the user tuned here.
    const labelPrioritySeededForRef = React.useRef(null);
    useEffect(() => {
        if (!localUsecase?._id || labelPrioritySeededForRef.current === localUsecase._id) return;
        labelPrioritySeededForRef.current = localUsecase._id;
        const defaults = trainingDefaultsFromLabelPriority(
            localUsecase.label_priority,
            rareClassEligibleLabels(localUsecase)
        );
        if (defaults) setState(prev => ({ ...prev, ...defaults }));
    }, [localUsecase]);

    const formRef = React.useRef(null);

    // Helper function to check for multilabel mismatches
    const hasMultilabelMismatch = () => {
        if (!localUsecase || !datasetsInfo) return false;

        // If usecase is already multilabel, no mismatch
        if (localUsecase.is_multilabel) return false;

        // Check if any dataset or benchmark is multilabel
        return datasetsInfo.some(datasetInfo => datasetInfo.is_multilabel);
    };

    // Training progress state. The visible progress UI is the cross-tab
    // banner rendered by the task page (TrainingProgressBanner);
    // this instance only gates the "Let's Train" button and registers a
    // freshly started training.
    const {
        showTrainingProgressCard,
        currentTrainingStatus,
        startNewTraining,
    } = useTrainingProgress(localUsecase);

    const handleCreateDistillationEvent = async (onEnd) => {
        try {
            await startTrainingProcess(
                localUsecase,
                {
                    train: state.train,
                    validation: state.validation,
                    metricOptimization: state.metricOptimization,
                    betaForFScore: state.betaForFScore,
                    trainingDevice: isGenerative ? 'gpu' : state.trainingDevice,
                    predictionStrategy: state.predictionStrategy,
                    baseModel: state.baseModel,
                    rareClassLabel: state.rareClassLabel,
                },
                // onDistillationSuccess
                (distillationEventId, newVersion) => {
                    // A manual start (e.g. "Start now" on the data-flows gate
                    // after arming) supersedes the scheduled auto-train — let
                    // the "Training Has Started!" modal show normally.
                    clearAutoTrainNotifyModalShown(localUsecase?._id);
                    updateStatus(localUsecase?._id, distillation_in_progress);
                    updateVersion(localUsecase?._id, newVersion);
                    setState(prev => ({ ...prev, baseModel: null }));
                    onTrainingStarted?.();
                },
                // onTrainingSuccess
                (distillationEventId) => {
                    scheduleUsecaseRefreshAfterTrainingStart({
                        usecaseId: localUsecase?._id,
                        initialDatasets: localUsecase?.datasets,
                        initialBenchmarks: localUsecase?.benchmarks,
                        refreshUsecases,
                        onDataOverviewRefresh,
                        expectAutoSplit: !(localUsecase?.benchmarks || []).length,
                    });
                    onEnd();
                    startNewTraining(distillationEventId);
                },
                // onError
                (errorMessage) => {
                    sparkToast.error(errorMessage);
                    onEnd();
                }
            );
        } catch (error) {
            console.log("error is:", error);
            sparkToast.error(error.detail || "Failed to create distillation event");
            onEnd();
        }
    };

    const validateBenchmarkOverlap = async () => {
        // Check if external model results are being used
        const isExternalModelResults = [ComparisonModeOption.manual_model_predictions, ComparisonModeOption.all].includes(
            localUsecase?.comparison_mode_option
        );

        if (!isExternalModelResults || !localUsecase?.manual_model_predictions?.length || localUsecase?.benchmarks?.length === 0) {
            return { shouldProceed: true };
        }

        // Validate benchmark overlap with external model predictions
        return new Promise((resolve) => {
            data_from_server_post(
                'validate-benchmark-external-match',
                {
                    client_id: localUsecase.client_id,
                    llm_usecase_id: localUsecase._id,
                    benchmark_names: localUsecase.benchmarks,
                    external_predictions_name: localUsecase.manual_model_predictions[0],
                },
                (response) => {
                    if (response?.success && response.results) {
                        // Calculate total overlapping across all benchmarks
                        let totalMatches = 0;
                        let totalBenchmarkExamples = 0;

                        Object.values(response.results).forEach(result => {
                            totalMatches += result.matches || 0;
                            totalBenchmarkExamples += result.benchmark_total || 0;
                        });

                        if (totalMatches === 0) {
                            // No overlap - show error modal and prevent training
                            setOverlapModalState({
                                visible: true,
                                overlappingCount: 0,
                                totalCount: totalBenchmarkExamples,
                                isError: true,
                            });
                            resolve({ shouldProceed: false, requiresConfirmation: false });
                        } else if (totalMatches < totalBenchmarkExamples) {
                            // Partial overlap - show warning modal and require confirmation
                            setOverlapModalState({
                                visible: true,
                                overlappingCount: totalMatches,
                                totalCount: totalBenchmarkExamples,
                                isError: false,
                            });
                            resolve({ shouldProceed: false, requiresConfirmation: true });
                        } else {
                            // Full overlap - proceed without modal
                            resolve({ shouldProceed: true });
                        }
                    } else {
                        // Validation failed - log error but allow training
                        console.error('Benchmark validation failed:', response?.error);
                        resolve({ shouldProceed: true });
                    }
                },
                (error) => {
                    console.error('Error validating benchmark overlap:', error);
                    // On network error, allow training to proceed
                    resolve({ shouldProceed: true });
                }
            );
        });
    };

    // A sub-task within a composed (multi-objective) task is required to have
    // a benchmark — the composed-task evaluation flow can't compare sub-task
    // models without one. Single-objective tasks remain unconstrained.
    const isPartOfComposedTask = (currentSubGroup?.usecases?.length || 0) > 1;
    const hasBenchmark = (localUsecase?.benchmarks?.length || 0) > 0;

    // Teacher comparison is required when the comparison toggle targets the
    // teacher, and always for generative tasks (LLM-as-judge needs a provider).
    const isTeacherRequired =
        (localUsecase?.comparison_mode &&
            [ComparisonModeOption.teacher, ComparisonModeOption.all].includes(localUsecase?.comparison_mode_option)) ||
        isGenerativeTask(localUsecase?.task_type);

    // No provider configured at all (all API keys / cloud credentials were
    // deleted, or none were ever added) while teacher comparison is required.
    // Shown as an indicative warning modal instead of silently disabling the
    // Let's Train button. Skipped while the catalog is still loading.
    const hasNoConfiguredProviders =
        !isProvidersLoading && Object.keys(availableProviders || {}).length === 0;

    const buildNoProvidersBlockedMessage = () => (
        <span>
            Add API keys or cloud credentials in
            <TrainBlockedLink
                onClick={handleGoToSettings}
                style={{ marginLeft: 4, marginRight: 4 }}
            >
                Account Settings
            </TrainBlockedLink>
            to enable teacher comparison, or disable it in
            <TrainBlockedLink
                onClick={handleGoToModelConfiguration}
                style={{ marginLeft: 4 }}
            >
                Model Configuration
            </TrainBlockedLink>
            .
        </span>
    );

    // Runs the final training gates (benchmark overlap validation) and, if they
    // pass, starts the training. Shared by the normal flow and the modal's
    // "Continue" action so the logic isn't duplicated.
    const proceedWithTrainingChecks = async () => {
        // Validate benchmark overlap with external model predictions
        const overlapValidation = await validateBenchmarkOverlap();

        if (!overlapValidation.shouldProceed) {
            setState(prev => ({
                ...prev,
                trainLoading: false
            }));
            // Modal will handle the rest - user can choose to continue or cancel
            return;
        }

        // Proceed with training
        await handleCreateDistillationEvent(() => {
            setState(prev => ({
                ...prev,
                trainLoading: false
            }));
        });
    };

    const handleFormSubmit = async (options = {}) => {
        const skipDataFlowsGate = options?.skipDataFlowsGate === true;
        setState(prev => ({
            ...prev,
            trainLoading: true
        }));
        setTrainBlockedMessage('');

        try {
            // Data flows still running (or finished but still settling into the
            // datasets — auto-train armed/triggering): let the user pick between
            // keeping the schedule and training now on the current (partial)
            // data before the regular gates run. A bare Let's Train must not
            // silently start a partial-data training; unchecking the modal's
            // box is the explicit way to train now.
            if (!skipDataFlowsGate) {
                const activeFlows = listActiveDataFlowsForUsecase(progressStatus, localUsecase?._id);
                const gate = resolveDataFlowsGate(activeFlows, localUsecase);
                if (gate.gate) {
                    setDataFlowsModal({
                        visible: true,
                        flowCount: gate.flowCount,
                        finalizing: gate.finalizing,
                    });
                    setState(prev => ({ ...prev, trainLoading: false }));
                    return;
                }
            }

            // "Optimize on" must name one of the task's labels; a stale pick
            // would otherwise be rejected (or silently ignored) at training.
            if (blockOnMissingOptimizeOnLabel(state.rareClassLabel, localUsecase)) {
                setState(prev => ({ ...prev, trainLoading: false }));
                return;
            }

            const schemaError = getEnabledJsonSchemaError(localUsecase);
            if (schemaError) {
                setTrainBlockedMessage(
                    <span>
                        The JSON schema is invalid. Fix it in the
                        <TrainBlockedLink
                            onClick={handleGoToModelConfiguration}
                            style={{ marginLeft: 4, marginRight: 4 }}
                        >
                            Model Configuration
                        </TrainBlockedLink>
                        step before continuing.
                    </span>
                );
                setState(prev => ({ ...prev, trainLoading: false }));
                return;
            }

            if (isPartOfComposedTask && !hasBenchmark) {
                setTrainBlockedMessage(
                    <span>
                        This sub-task needs a benchmark before training can start. Add one in the
                        <TrainBlockedLink onClick={handleGoToBenchmark} style={{ marginLeft: 4 }}>
                            Data Editor
                        </TrainBlockedLink>
                    </span>
                );
                setState(prev => ({ ...prev, trainLoading: false }));
                return;
            }

            // Teacher comparison is on but no provider is configured at all.
            if (isTeacherRequired && hasNoConfiguredProviders) {
                setTrainBlockedMessage(buildNoProvidersBlockedMessage());
                setState(prev => ({ ...prev, trainLoading: false }));
                return;
            }

            // Validate training start
            const validation = validateTrainingStart(
                localUsecase,
                datasetMetadata,
                benchmarkMetadata,
                localUsecase?.benchmarks,
                { availableProviders, isLoading: isProvidersLoading },
            );
            if (!validation.success) {
                if (validation.reason === 'missing_training_data') {
                    setTrainBlockedMessage(
                        <span>
                            Go to
                            <TrainBlockedLink
                                onClick={handleGoToDataSelection}
                                style={{ marginLeft: 4, marginRight: 4 }}
                            >
                                Data Selection
                            </TrainBlockedLink>
                            Tab and choose your data for training
                        </span>
                    );
                } else if (validation.reason === 'missing_prompt') {
                    setTrainBlockedMessage(
                        <span>
                            You must go to
                            <TrainBlockedLink
                                onClick={handleGoToModelConfiguration}
                                style={{ marginLeft: 4, marginRight: 4 }}
                            >
                                Model Configuration
                            </TrainBlockedLink>
                            Step and fill the 'Prompt' field
                        </span>
                    );
                } else if (validation.reason === 'missing_teacher_model') {
                    setTrainBlockedMessage(
                        <span>
                            You must go to
                            <TrainBlockedLink
                                onClick={handleGoToModelConfiguration}
                                style={{ marginLeft: 4, marginRight: 4 }}
                            >
                                Model Configuration
                            </TrainBlockedLink>
                            Step and fill the 'Commercial Model' field
                        </span>
                    );
                } else if (validation.reason === 'missing_parser_function') {
                    setTrainBlockedMessage(
                        <span>
                            You must go to
                            <TrainBlockedLink
                                onClick={handleGoToModelConfiguration}
                                style={{ marginLeft: 4, marginRight: 4 }}
                            >
                                Model Configuration
                            </TrainBlockedLink>
                            Step and fill the 'Response Parser' function
                        </span>
                    );
                } else if (validation.reason === 'teacher_provider_not_configured') {
                    setTrainBlockedMessage(
                        <span>
                            The selected commercial model uses the {validation.providerLabel} provider, but no{' '}
                            {validation.providerLabel} credentials are configured. Add credentials in
                            <TrainBlockedLink
                                onClick={handleGoToSettings}
                                style={{ marginLeft: 4, marginRight: 4 }}
                            >
                                Settings
                            </TrainBlockedLink>
                            or select a different commercial model in the
                            <TrainBlockedLink
                                onClick={handleGoToModelConfiguration}
                                style={{ marginLeft: 4, marginRight: 4 }}
                            >
                                Model Configuration
                            </TrainBlockedLink>
                            step
                        </span>
                    );
                } else if (validation.reason === 'judge_provider_not_configured') {
                    setTrainBlockedMessage(
                        validation.showModelConfigurationHint ? (
                            <span>
                                The current model provider isn’t supported for {validation.taskTypeLabel} training.
                                Add credentials in
                                <TrainBlockedLink
                                    onClick={handleGoToSettings}
                                    style={{ marginLeft: 4, marginRight: 4 }}
                                >
                                    Settings
                                </TrainBlockedLink>
                                or select a different commercial model in the
                                <TrainBlockedLink
                                    onClick={handleGoToModelConfiguration}
                                    style={{ marginLeft: 4, marginRight: 4 }}
                                >
                                    Model Configuration
                                </TrainBlockedLink>
                                step
                            </span>
                        ) : (
                            <span>
                                The current model provider isn’t supported for {validation.taskTypeLabel} training.
                                Add credentials in
                                <TrainBlockedLink
                                    onClick={handleGoToSettings}
                                    style={{ marginLeft: 4 }}
                                >
                                    Settings
                                </TrainBlockedLink>
                                .
                            </span>
                        )
                    );
                } else {
                    // Any other validation failure surfaces in the warning modal.
                    setTrainBlockedMessage(validation.message);
                }
                setState(prev => ({ ...prev, trainLoading: false }));
                return;
            }

            if (isGenerative) {
                try {
                    const summariesResponse = await new Promise((resolve, reject) => {
                        data_from_server_post(
                            "check-has-summaries",
                            localUsecase._id,
                            resolve,
                            reject
                        );
                    });
                    if (summariesResponse?.has_summaries === false) {
                        const noun = getGenerativeOutputNoun(localUsecase?.task_type);
                        setTrainBlockedMessage(
                            `Can't start training. No ${noun === 'summary' ? 'summaries' : 'responses'} found in your dataset. Re-upload the dataset to contain text in the ${noun.charAt(0).toUpperCase() + noun.slice(1)} field, then try again.`
                        );
                        setState(prev => ({ ...prev, trainLoading: false }));
                        return;
                    }
                } catch (e) {
                    console.error('Error checking summaries:', e);
                }
            }

            // Block training if benchmark/external have labels/entities/relations not present in the dataset.
            // This mirrors the Data Collection "error" insights behavior.
            const fetchCounts = (params) =>
                new Promise((resolve, reject) => {
                    data_from_server_post(
                        'get-label-counts',
                        params,
                        (resp) => resolve(resp || {}),
                        reject
                    );
                });

            let unlabeledTotal = 0;
            try {
                const [datasetCountsResp, benchmarkCountsResp, externalCountsResp] = await Promise.all([
                    fetchCounts({ llm_usecase_id: localUsecase._id, is_benchmark: false }),
                    localUsecase?.benchmarks?.length
                        ? fetchCounts({ llm_usecase_id: localUsecase._id, is_benchmark: true })
                        : Promise.resolve(null),
                    (localUsecase?.manual_model_predictions?.length && [ComparisonModeOption.manual_model_predictions, ComparisonModeOption.all].includes(localUsecase?.comparison_mode_option))
                        ? fetchCounts({ llm_usecase_id: localUsecase._id, is_benchmark: false, is_manual_model_predictions: true })
                        : Promise.resolve(null),
                ]);

                unlabeledTotal = datasetCountsResp?.unlabeled_count?.total || 0;

                const mismatchMessages = getLabelTypeMismatchMessages({
                    taskType: localUsecase?.task_type,
                    datasetCounts: datasetCountsResp || {},
                    benchmarkCounts: benchmarkCountsResp,
                    externalCounts: externalCountsResp,
                });

                if (mismatchMessages.length > 0) {
                    setTrainBlockedMessage(
                        <span>
                            Please resolve the error insights in{' '}
                            <TrainBlockedLink onClick={handleGoToDataCollection}>
                                Data Analysis
                            </TrainBlockedLink>{' '}
                            under the 'Data Overview' card before continuing.
                        </span>
                    );
                    setState(prev => ({ ...prev, trainLoading: false }));
                    return;
                }
            } catch (e) {
                // If this check fails, don't hard-block training.
                console.error('Error checking label/entity/relation mismatches before training:', e);
            }

            // Warn when more than 10% of the active train-set records are unlabeled
            // (classification only — the "Unlabeled" [] bucket is a labels concept).
            if (localUsecase?.task_type === TaskType.classification) {
                const totalRecords = datasetMetadata?.num_records || 0;
                if (totalRecords > 0 && unlabeledTotal / totalRecords > 0.10) {
                    setUnlabeledModal({
                        visible: true,
                        unlabeledCount: unlabeledTotal,
                        total: totalRecords,
                        percent: Math.round((unlabeledTotal / totalRecords) * 100),
                    });
                    setState(prev => ({ ...prev, trainLoading: false }));
                    return;
                }
            }

            // Loading stays on until create_distillation_event / create_training
            // callbacks call onEnd (those helpers are callback-based, not awaited).
            await proceedWithTrainingChecks();
        } catch (error) {
            console.error('Failed to start training:', error);
            const message = error?.detail || error?.message || 'Failed to start training';
            setTrainBlockedMessage(message);
            setState(prev => ({ ...prev, trainLoading: false }));
        }
    };

    // Auto-train after data flows: schedule the training instead of waiting for
    // the running Generate-with-AI / Retrieval Agent flows to finish. Runs the
    // config validations only — data-dependent gates make no sense while the
    // data is still being produced (the backend re-reads datasets at trigger
    // time).
    const [armLoading, setArmLoading] = useState(false);
    // Arm confirmation — the same Notify Me modal the task page shows on
    // training start, surfaced immediately at arm time with a "will start"
    // title (the later "Training Has Started!" duplicate is suppressed via
    // markAutoTrainNotifyModalShown).
    const [autoTrainScheduledModalOpen, setAutoTrainScheduledModalOpen] = useState(false);

    // "Notify Me" on the arm confirmation — register the logged-in user's
    // email for a training-completion email. The registration is
    // usecase-scoped, so it applies to the training the auto-train trigger
    // eventually starts. Only offered when we actually have an email (auth
    // disabled sessions have an empty one).
    const canNotifyByEmail = Boolean(user?.email && localUsecase?._id);
    const handleAutoTrainNotifyMe = () => {
        setAutoTrainScheduledModalOpen(false);
        data_from_server_post(
            'notify_on_training_completion',
            {
                client_id: localUsecase?.client_id,
                usecase_id: localUsecase?._id,
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success("We'll email you when your model is ready");
                } else if (result?.message) {
                    sparkToast.error(result.message);
                }
            },
            () => sparkToast.error('Failed to register the notification')
        );
    };

    const handleArmAutoTrain = (options = {}) => {
        // When the arm request comes from the Let's Train gate modal and the
        // flows already finished meanwhile, fall through to an immediate
        // training instead of telling the user to click again.
        const trainWhenIdle = options?.trainWhenIdle === true;
        if (armLoading || !localUsecase) return;
        setTrainBlockedMessage('');

        if (blockOnMissingOptimizeOnLabel(state.rareClassLabel, localUsecase)) return;

        const schemaError = getEnabledJsonSchemaError(localUsecase);
        if (schemaError) {
            setTrainBlockedMessage(
                <span>
                    The JSON schema is invalid. Fix it in the
                    <TrainBlockedLink
                        onClick={handleGoToModelConfiguration}
                        style={{ marginLeft: 4, marginRight: 4 }}
                    >
                        Model Configuration
                    </TrainBlockedLink>
                    step before continuing.
                </span>
            );
            return;
        }
        if (isPartOfComposedTask && !hasBenchmark) {
            setTrainBlockedMessage(
                <span>
                    This sub-task needs a benchmark before training can start. Add one in the
                    <TrainBlockedLink onClick={handleGoToBenchmark} style={{ marginLeft: 4 }}>
                        Data Editor
                    </TrainBlockedLink>
                </span>
            );
            return;
        }
        if (isTeacherRequired && hasNoConfiguredProviders) {
            setTrainBlockedMessage(buildNoProvidersBlockedMessage());
            return;
        }
        const configValidation = validateAutoTrainConfig(localUsecase, {
            availableProviders,
            isLoading: isProvidersLoading,
        });
        if (!configValidation.success) {
            setTrainBlockedMessage(configValidation.message);
            return;
        }

        setArmLoading(true);
        armAutoTrain({
            usecase: localUsecase,
            trainingParams: {
                train: state.train,
                validation: state.validation,
                metricOptimization: state.metricOptimization,
                betaForFScore: state.betaForFScore,
                trainingDevice: isGenerative ? 'gpu' : state.trainingDevice,
                predictionStrategy: state.predictionStrategy,
                baseModel: state.baseModel,
                rareClassLabel: state.rareClassLabel,
            },
            onSuccess: (result) => {
                setArmLoading(false);
                const { action, toast } = resolveArmResult(result);
                // "No active flows" without a pending schedule: nothing is
                // settling, so the modal's "wait" choice falls through to an
                // immediate training. A `triggering` result must NOT do that —
                // the settle thread is about to start the training itself.
                if (action === 'train_now' && trainWhenIdle) {
                    void handleFormSubmitRef.current({ skipDataFlowsGate: true });
                    return;
                }
                if (action === 'triggering' || action === 'scheduled') {
                    updateUsecase?.(localUsecase._id, {
                        auto_train_pending: result.auto_train_pending || {
                            ...(localUsecase.auto_train_pending || {}),
                            triggering: action === 'triggering',
                        },
                    });
                    // Confirmation + Notify Me opportunity now instead of the
                    // "Training Has Started!" modal when the data is ready —
                    // the flag suppresses that later duplicate for this tab.
                    markAutoTrainNotifyModalShown(localUsecase._id);
                    setAutoTrainScheduledModalOpen(true);
                    return;
                }
                sparkToast[toast.type](toast.text);
            },
            onError: (message) => {
                setArmLoading(false);
                sparkToast.error(message);
            },
        });
    };

    const handleDataFlowsModalCancel = () => {
        setDataFlowsModal({ visible: false, flowCount: 0, finalizing: false });
    };

    // "Start" on the data-flows gate: checked box schedules auto-train,
    // unchecked box trains right now on the current (partial) data.
    const handleDataFlowsModalStart = (waitForData) => {
        setDataFlowsModal({ visible: false, flowCount: 0, finalizing: false });
        if (waitForData) {
            handleArmAutoTrain({ trainWhenIdle: true });
            return;
        }
        void handleFormSubmit({ skipDataFlowsGate: true });
    };

    const handleUnlabeledModalContinue = async () => {
        setUnlabeledModal(prev => ({ ...prev, visible: false }));
        setState(prev => ({
            ...prev,
            trainLoading: true
        }));
        await proceedWithTrainingChecks();
    };

    const handleUnlabeledModalReview = () => {
        setUnlabeledModal(prev => ({ ...prev, visible: false }));
        setState(prev => ({
            ...prev,
            trainLoading: false
        }));
        handleReviewUnlabeled();
    };

    const handleUnlabeledModalCancel = () => {
        setUnlabeledModal(prev => ({ ...prev, visible: false }));
    };

    const handleOverlapModalContinue = async () => {
        setOverlapModalState({ ...overlapModalState, visible: false });
        setState(prev => ({
            ...prev,
            trainLoading: true
        }));

        // User confirmed, proceed with training
        await handleCreateDistillationEvent(() => {
            setState(prev => ({
                ...prev,
                trainLoading: false
            }));
        });
    };

    const handleOverlapModalCancel = () => {
        setOverlapModalState({ ...overlapModalState, visible: false });
    };

    const getTableData = () => [];

    // Register handleFormSubmit as the drawer header "Next" action when embedded.
    const handleFormSubmitRef = React.useRef(handleFormSubmit);
    handleFormSubmitRef.current = handleFormSubmit;
    // The progress card stays visible after training reaches a final status
    // (completed/canceled/failed) so the user can see the outcome, but the
    // "Let's Train" button should only be blocked while training is running.
    const isTrainingActive =
        showTrainingProgressCard &&
        !FINAL_TRAINING_STATUSES.includes(String(currentTrainingStatus || '').toLowerCase());
    // Note: "teacher required but no provider configured" no longer disables
    // the button — clicking Let's Train shows an indicative TrainBlockedModal
    // instead (see the no-providers gate in handleFormSubmit).
    const isSubmitDisabled =
        isTrainingActive ||
        !!(datasetMetadata && datasetMetadata.num_characters === 0);
    useEffect(() => {
        if (layout === 'summary-modal' || !embeddedInTaskDrawer || setDrawerHeaderNext == null) return undefined;
        setDrawerHeaderNext({
            onClick: () => { void handleFormSubmitRef.current(); },
            loading: state.trainLoading,
            disabled: isSubmitDisabled || role === 'Viewer' || readOnly,
            loadingLabel: 'Training…',
            label: "Let's Train",
        });
        return () => setDrawerHeaderNext(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layout, embeddedInTaskDrawer, setDrawerHeaderNext, state.trainLoading, isSubmitDisabled, role, readOnly]);

    useEffect(() => {
        onTrainActionChange?.({
            onClick: () => { void handleFormSubmitRef.current(); },
            loading: state.trainLoading,
            disabled: isSubmitDisabled || role === 'Viewer',
        });
    }, [onTrainActionChange, state.trainLoading, isSubmitDisabled, role]);

    if (!localUsecase) {
        return <LoadingTaskData />;
    }

    // Student prompt overrides (generative tasks only). Shown as separate
    // fields under the teacher prompts, but only when they actually differ
    // from the inherited teacher value AND "Include prompt in training &
    // evaluation" is checked — otherwise the student info is not used in
    // training so displaying it would be misleading.
    const teacherSystemPrompt = localUsecase?.teacher_system_prompt || '';
    const teacherPrompt = localUsecase?.prompt || '';
    const teacherSchema = localUsecase?.teacher_schema || '';
    const isStudentOverrideShown = (override, teacherValue) =>
        override != null &&
        String(override).trim() !== '' &&
        String(override).trim() !== String(teacherValue).trim();
    const showStudentSystemPrompt =
        isGenerative &&
        includeSystemPrompt &&
        isStudentOverrideShown(localUsecase?.generative_student_instructions, teacherSystemPrompt);
    const showStudentPrompt =
        isGenerative &&
        includeSystemPrompt &&
        isStudentOverrideShown(localUsecase?.generative_student_prompt, teacherPrompt);
    const showStudentSchema =
        isGenerative &&
        isStudentOverrideShown(localUsecase?.generative_schema, teacherSchema);

    const usecaseForMetricsCriteria = classicMetricsOverride
        ? { ...localUsecase, classic_metrics_config: classicMetricsOverride }
        : localUsecase;

    // Offered while generation/retrieval flows are running (hidden during an
    // active training — the backend rejects arming then anyway).
    const autoTrainSection = !isTrainingActive ? (
        <AutoTrainScheduleSection
            usecase={localUsecase}
            onArm={handleArmAutoTrain}
            arming={armLoading}
            readOnly={role === 'Viewer'}
        />
    ) : null;

    const renderModelSummary = (promptsFooter = null) => (
        <ModelSummarySection
            loading={state.loading}
            usecase={usecaseForMetricsCriteria}
            availableProviders={availableProviders}
            teacherSystemPrompt={teacherSystemPrompt}
            teacherPrompt={teacherPrompt}
            teacherSchema={teacherSchema}
            showStudentSystemPrompt={showStudentSystemPrompt}
            showStudentPrompt={showStudentPrompt}
            showStudentSchema={showStudentSchema}
            hasMultilabelMismatch={hasMultilabelMismatch()}
            trainingDevice={state.trainingDevice}
            metricOptimization={state.metricOptimization}
            betaForFScore={state.betaForFScore}
            rareClassLabel={state.rareClassLabel}
            predictionStrategy={state.predictionStrategy}
            baseModel={state.baseModel}
            promptsFooter={promptsFooter}
        />
    );

    const advancedSection = (
        <Advanced
            metricOptimization={state.metricOptimization}
            onMetricOptimizationChange={(value) => setState(prev => ({
                ...prev,
                metricOptimization: value
            }))}
            betaForFScore={state.betaForFScore}
            onFScoreBetaChange={(value) => setState(prev => ({
                ...prev,
                betaForFScore: value
            }))}
            rareClassLabel={state.rareClassLabel}
            onRareClassLabelChange={(value) => setState(prev => ({
                ...prev,
                rareClassLabel: value
            }))}
            rareClassLabelOptions={rareClassEligibleLabels(localUsecase)}
            trainingDevice={state.trainingDevice}
            onHardwareDeviceChange={(value) => setState(prev => ({
                ...prev,
                trainingDevice: value
            }))}
            predictionStrategy={state.predictionStrategy}
            onPredictionStrategyChange={(value) => setState(prev => ({
                ...prev,
                predictionStrategy: value
            }))}
            train={state.train}
            setTrain={(e) => setState(prev => ({ ...prev, train: Math.min(100, Math.max(0, e.target.value)) }))}
            setValidation={(e) => setState(prev => ({ ...prev, validation: Math.min(100 - prev.train, Math.max(0, e.target.value)) }))}
            validation={state.validation}
            showTest={true}
            checkboxOnChange={(newState) => setState(prev => ({
                ...prev,
                ...newState
            }))}
            taskType={localUsecase?.task_type}
            baseModelSelector={
                <BaseModelSelector
                    taskType={localUsecase?.task_type}
                    value={state.baseModel}
                    onChange={(value) => setState(prev => ({ ...prev, baseModel: value }))}
                />
            }
            accuracyMetricsSelector={
                localUsecase?.task_type === TaskType.contentGeneration ? (
                    <MetricsCriteriaSelector
                        scope="accuracy"
                        usecase={usecaseForMetricsCriteria}
                        onPersisted={onMetricsCriteriaPersisted}
                    />
                ) : null
            }
            judgeCriteriaSelector={
                isGenerative ? (
                    <JudgeCriteriaSelector usecase={localUsecase} onPersisted={() => refreshUsecases?.()} />
                ) : null
            }
            classicMetricsSelector={
                isGenerative ? (
                    <MetricsCriteriaSelector
                        scope="classic"
                        usecase={usecaseForMetricsCriteria}
                        onPersisted={onMetricsCriteriaPersisted}
                    />
                ) : null
            }
        />
    );

    const trainingGateModals = (
        <>
            <BenchmarkOverlapModal
                visible={overlapModalState.visible}
                onCancel={handleOverlapModalCancel}
                onContinue={handleOverlapModalContinue}
                overlappingCount={overlapModalState.overlappingCount}
                totalCount={overlapModalState.totalCount}
                isError={overlapModalState.isError}
            />
            <UnlabeledDataWarningModal
                visible={unlabeledModal.visible}
                onCancel={handleUnlabeledModalCancel}
                onContinue={handleUnlabeledModalContinue}
                onReview={handleUnlabeledModalReview}
                unlabeledCount={unlabeledModal.unlabeledCount}
                total={unlabeledModal.total}
                percent={unlabeledModal.percent}
            />
            <DataFlowsRunningModal
                visible={dataFlowsModal.visible}
                flowCount={dataFlowsModal.flowCount}
                finalizing={dataFlowsModal.finalizing}
                onCancel={handleDataFlowsModalCancel}
                onStart={handleDataFlowsModalStart}
            />
            <TrainBlockedModal
                message={trainBlockedMessage}
                onClose={() => setTrainBlockedMessage('')}
            />
            <OptimizeOnLabelMissingModal
                mismatch={optimizeOnMismatch}
                surface="train"
                onClose={() => setOptimizeOnMismatch(null)}
            />
            {/* Auto-train armed: immediate confirmation with the Notify Me
                action (Figma 3001-68567 chrome, "will start" title). */}
            <ModelProcessingModal
                title="Training will start when data is ready"
                open={autoTrainScheduledModalOpen}
                onClose={() => setAutoTrainScheduledModalOpen(false)}
                onNotifyMe={canNotifyByEmail ? handleAutoTrainNotifyMe : undefined}
            />
        </>
    );

    if (layout === 'summary-modal') {
        return (
            <>
                {autoTrainSection}
                {renderModelSummary(advancedSection)}
                {trainingGateModals}
            </>
        );
    }

    // Embedded in the task page: padding comes from task-page.css
    // (`.task-page-tab-body > .train`).
    const trainClassName = embeddedInTaskDrawer ? 'train' : 'train page-content-shell';

    return (
        <div className={trainClassName}>
            <div className="train__content">
                {autoTrainSection}
                {/* White card chrome per Figma 3001-71663 (Card Default, 1200px). */}
                <CardSurface
                    background="white"
                    bordered={false}
                    padding="large"
                    className="train-model-summary-card"
                >
                    {renderModelSummary()}
                    {advancedSection}
                </CardSurface>
                {/* Submit row — active-training progress lives in the
                    cross-tab header banner, not in this tab. */}
                <div className="train__actions">
                    <div className="train__actions-spacer" />
                    <div className="train__actions-submit">
                        <div className="train__actions-submit-inner">
                            {/* Blocked-training feedback shows in TrainBlockedModal (trainingGateModals). */}
                            {!embeddedInTaskDrawer && (
                                <Submit
                                    onClick={handleFormSubmit}
                                    disabled={isSubmitDisabled}
                                    loading={state.trainLoading}
                                    loadingText="Training"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {trainingGateModals}
        </div>
    );
};
